// src/pages/ReprorationConsolePage.tsx
//
// ★★★ WAVE 14 — THE RE-PRORATION CONSOLE. P2C1 §12, Recovery Agency interface
// row 7: *"Alert file on re-prorated HSP bills due to changes in officers'
// entitlement records"*, PRS → RA, Batch/SFTP, **Daily**.
//
// ══ WHAT THIS CLOSES, AND WHY IT IS A SCREEN AT ALL ══
//
// Wave 13 built the whole capability and gave it no screen. `POST
// /api/bills/reprorate-sweep` and `POST /api/reproration-alerts/:code` were
// reachable only from `scripts/run-reproration-sweep.mjs` or by hand, which means
// a demonstration on a laptop could not show the one thing §12's RA row 7 asks
// for. `docs/delivery/DEMO-READINESS-PLAN.md`'s own rule is that an unreachable
// route is a surface the customer does not have.
//
// ⚠ **`lib/api.ts` says the sweep is "deliberately not reachable from a bill's
// own screen", and that is still true and is not what changed.** Re-dividing ONE
// bill because its officer's entitlement moved, while leaving that officer's
// other bills alone, is not a coherent operation and this product still does not
// offer it. What exists now is one ESTATE-WIDE console, which is the shape the
// operation actually has.
//
// ══ ★★★ THIS SCREEN MOVES PUBLIC MONEY, SO THE DEFAULT IS A DRY RUN ══
//
// Every other operator control in this product either produces an artefact
// (`SettlementBatchesPage`), seals one (`AuditArchivePage`) or records a
// notifiable event (the storage sweep). This one **re-divides money between the
// public purse and officers**, in bulk, on a click. So:
//
//   * **Dry run is CHECKED when the screen loads.** An operator who presses the
//     button without reading anything gets a decision and a report and no write.
//     Clearing it is a deliberate act with a warning attached to the control.
//   * **The write button says what it will do**, and the count of bills whose
//     money will move is shown by the dry run BEFORE the real one is offered.
//
// ══ ⚠ WHAT THIS SCREEN MUST NOT LET A READER CONCLUDE ══
//
//   * **That anything runs on a schedule.** §12 asks for this daily. There is no
//     scheduler in this deployment (ADR 0009 Decision 4) and the server says so
//     in its own `scheduler` field, rendered verbatim below in every state.
//   * **That "0 re-prorated" means "the estate agrees".** It can equally mean
//     nothing was assessable, or that the bills which moved were past this run's
//     bound. All eleven counts are rendered for that reason.
//   * **That a produced alert file has been sent.** It exists in
//     `app.outbound_file` and has reached nobody; `scripts/publish-outbox.mjs
//     --apply` hands it over, and where the channel is a local directory — which
//     is every environment this repository ships, `PRS2_SFTP_HOST` unset,
//     Bucket B-3 — it still reaches no Recovery Agency.
//
// ══ WHY THERE IS NO LIMIT CONTROL, WHICH IS A DELIBERATE OMISSION ══
//
// The route bounds one run and publishes its own ceiling in prose. A number box
// on this screen would put a SECOND copy of that ceiling in the bundle, which is
// the S-011 defect class `published-bounds.test.ts` exists to keep at zero. So
// the console runs the sweep at the route's own default and offers
// **Continue from here**, which passes back the `nextAfterReference` the server
// itself returned. The operator never types a bound and the screen never states
// one.
import { plainCode, plainProse } from '../lib/plain-words'
import { CategoryBadge } from '../components/StatusBadge'
import { useState, type ReactElement } from 'react'
import { SgdsAlert, SgdsButton, SgdsInput, SgdsModal } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { ErrorCard } from '../components/StateCards'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { useClientSort, type ClientSortColumns } from '../lib/useClientSort'
import { formatCents } from '../lib/format'
import { targetValue } from '../lib/sgds-dom'
import type { ReprorationAlertResponse, ReprorationSweepEntry, ReprorationSweepResponse } from '../lib/types'

/**
 * ⚠ **THE STANDING BOUNDARY, rendered in every state of this screen.**
 *
 * `AuditArchivePage`'s own version of this states the rule it follows: the moment
 * a notice becomes conditional, the state where it matters most is the state that
 * hides it. Here it must be true before a sweep has been run, after one that
 * moved nothing, and after one that moved six figures.
 */
function ConsoleBoundary(): ReactElement {
  return (
    <div className="pt-unbuilt">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">
        No scheduler runs any of this, and this screen moves money
      </p>
      <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
        The Recovery Agency interface asks for this <strong>daily</strong>. Nothing in this deployment runs it
        on a timer: it happens when an operator presses a button here, or when{' '}
        <code className="pt-mono text-xs">scripts/run-reproration-sweep.mjs</code> is invoked, and at no other time.
        That is a deliberate choice, not an omission: wiring a timer is trivial and deciding what it should do when a
        sweep fails halfway is not, so it is a decision for deployment rather than one made here by default. A sweep
        <strong>re-divides public money</strong> between the government and officers
        wherever a bill&apos;s recorded entitlement disagrees with the entitlement in force today, so{' '}
        <strong>Dry run is on when this screen loads</strong> and clearing it is a deliberate act.
      </p>
      <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
        An alert file is <strong>produced, not sent</strong>. It is held in the outbound
        file register and has reached nobody; an operator hands it over
        with <code className="pt-mono text-xs">scripts/publish-outbox.mjs --apply</code>, and where that publishes to a
        local directory the channel declares <code className="pt-mono text-xs">collector: nobody</code>, which is
        every environment this repository ships.
      </p>
      <p className="mt-2 font-semibold">
        Treat this as an operator console, not as evidence that entitlement changes are being reconciled on a schedule.
      </p>
    </div>
  )
}

/**
 * ★★ THE ELEVEN COUNTS, EVERY ONE OF THEM RENDERED.
 *
 * ⚠ A console showing only `reprorated` and `referred` would report **0 and 0**
 * identically for three different estates: one where every recorded entitlement
 * still agrees, one where nothing carries a recorded entitlement to compare
 * against at all, and a run whose bound stopped short of the bills that moved.
 * Each of those needs a different next action from the operator, so each has its
 * own row and its own sentence.
 */
const COUNT_ROWS: readonly {
  readonly key: keyof ReprorationSweepResponse['counts']
  readonly label: string
  readonly note: string
  /** Rendered in the danger colour when non-zero — something is owed to a person. */
  readonly attention?: boolean
}[] = [
  {
    key: 'examined',
    label: 'Examined by this run',
    note: 'Recomputed against the entitlement in force today.',
  },
  {
    key: 'eligible',
    label: 'Eligible in the whole estate',
    note: 'Every bill in your scope that has a split and could still take a new one.',
  },
  {
    key: 'notReached',
    label: 'Not reached by this run',
    note: '⚠ Unexamined, NOT unchanged. Nothing is known about these until a continuation reaches them.',
    attention: true,
  },
  {
    key: 'reprorated',
    label: 'Re-prorated: money moved',
    note: 'The split was changed. Each one is in the ledger on the bill and in the audit trail.',
  },
  {
    key: 'referred',
    label: 'Referred: a person is needed',
    note:
      'The split MUST change and PRS did not change it: money was already instructed against the old division, ' +
      'or a correction chain hangs off the bill.',
    attention: true,
  },
  {
    key: 'alreadyReferred',
    label: 'Already referred',
    note:
      'This exact referral has been made before and the Recovery Agency has already been told. A referral changes ' +
      'nothing, so the disagreement is still there: it is not re-reported.',
  },
  {
    key: 'unchanged',
    label: 'Unchanged',
    note: 'The recorded entitlement still matches the one in force. Nothing was owed and nothing was done.',
  },
  {
    key: 'entitlementChangedNoMoneyMoved',
    label: 'Entitlement changed, money did not',
    note: 'A re-grade within the same subsidy rate. The fingerprint was refreshed; not a penny moved.',
  },
  {
    key: 'unassessable',
    label: 'Unassessable',
    note:
      'Computed before PRS recorded WHICH entitlement produced the split, and nothing was back-filled: writing ' +
      "today's entitlement onto a months-old split would have destroyed the comparison. Not \"unchanged\": nothing " +
      'can be said about them from the record.',
  },
  {
    key: 'refused',
    label: 'Cannot be priced today',
    note: 'No rule in force for the scheme, or a cap whose parameter has not been supplied.',
    attention: true,
  },
  {
    key: 'moved',
    label: 'Changed under the write',
    note: 'The bill changed while the sweep was deciding about it, so it was left alone rather than written blind.',
  },
]

type EntrySortKey = 'reference' | 'officerId' | 'agencyCode' | 'outcome' | 'delta' | 'newCoPayment'

const ENTRY_COLUMN_LABELS: Readonly<Record<EntrySortKey, string>> = {
  reference: 'Bill',
  officerId: 'Officer',
  agencyCode: 'Provider',
  outcome: 'Outcome',
  delta: 'Co-payment movement',
  newCoPayment: 'New co-payment',
}

/**
 * ⚠ **A CLIENT-SIDE SORT THAT IS HONEST, and the reason has to be stated rather
 * than assumed.**
 *
 * `lib/useClientSort.ts`'s header says a browser sort is a lie when the server caps
 * the list, because it reorders one page while looking like it reordered everything.
 * That does not apply here: `entries` is not a page of a larger result — it is the
 * COMPLETE set of bills this run moved or referred, returned whole in the sweep's own
 * response. There is no second page to be wrong about, and no sort parameter on the
 * route to defer to.
 *
 * What IS capped is the run, and that is reported separately and loudly as
 * `notReached`.
 *
 * ★★ **THIS WAS A HAND-ROLLED COMPARATOR UNTIL 2026-08-24**, and folding it onto
 * `useClientSort` is a consistency fix rather than a bug fix — every column below is
 * `NOT NULL`, so the old version had no live defect. It is worth doing anyway: this
 * app had THREE private comparators (here, `SettlementBatchesPage` and
 * `AccessControlMatrixPage`), two of them with no null branch at all, and the next
 * one copied from either template would have inherited that. One comparator carries
 * the nulls-last rule; three carry three opinions about it.
 */
const ENTRY_SORT_COLUMNS: ClientSortColumns<ReprorationSweepEntry, EntrySortKey> = {
  reference: { type: 'text', valueOf: (entry) => entry.reference },
  officerId: { type: 'text', valueOf: (entry) => entry.officerId },
  agencyCode: { type: 'text', valueOf: (entry) => entry.agencyCode },
  outcome: { type: 'text', valueOf: (entry) => entry.outcome },
  /*
    ⚠ THE MOVEMENT, not either figure it is computed from. A reader clicking this
    header is asking which bills moved the FURTHEST, and a signed delta is the only
    value that answers it: sorting `newCoPayment` would rank a bill that barely moved
    above one that fell by hundreds, and both cells stay legible either way.
  */
  delta: { type: 'number', valueOf: (entry) => entry.newCoPayment - entry.priorCoPayment },
  newCoPayment: { type: 'number', valueOf: (entry) => entry.newCoPayment },
}

/** Whole cents as a SIGNED figure, so a reader never has to work out the direction. */
function signedMoney(cents: number): string {
  return `${cents > 0 ? '+' : cents < 0 ? '−' : ''}${formatCents(Math.abs(cents))}`
}

function SweepReport({
  report,
  onContinue,
  continuing,
}: {
  readonly report: ReprorationSweepResponse
  readonly onContinue: (afterReference: string) => void
  readonly continuing: boolean
}): ReactElement {
  const { rows: entries, sort, onSort, interacted } = useClientSort(report.entries, ENTRY_SORT_COLUMNS)

  return (
    <div className="flex flex-col gap-4">
      {report.dryRun && (
        <SgdsAlert show variant="warning">
          <span>
            <strong>Dry run: nothing was written.</strong> No split was changed, no ledger row was created and
            no audit event was recorded. The counts below are what a real run <em>would</em> do against the estate as it
            stands now.
          </span>
        </SgdsAlert>
      )}

      <div>
        <h3 className="text-lg font-semibold">
          What this run found
          {report.afterReference !== null && (
            <span className="ml-2 text-sm font-normal text-[var(--sgds-body-color-subtle)]">
              continued after <code className="pt-mono text-xs">{report.afterReference}</code>
            </span>
          )}
        </h3>
        <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-6 gap-y-2 max-w-3xl">
          {COUNT_ROWS.map((row) => {
            const n = report.counts[row.key]
            return (
              <div key={row.key} className="col-span-2 grid grid-cols-subgrid items-start">
                <dt className="text-sm">
                  {row.label}
                  <span className="block text-xs text-[var(--sgds-body-color-subtle)]">{plainProse(row.note)}</span>
                </dt>
                <dd
                  className={
                    row.attention === true && n > 0
                      ? 'pt-num text-sm font-semibold text-[var(--sgds-danger-color-default)]'
                      : 'pt-num text-sm font-semibold'
                  }
                >
                  {n}
                </dd>
              </div>
            )
          })}
        </dl>
      </div>

      {/*
        ⚠ THE SERVER'S OWN TWO SENTENCES, VERBATIM. The boundary is the difference
        between "the estate agrees" and "this run stopped early", and the scheduler
        note is the difference between a console and a nightly job. Neither is
        paraphrased, because a paraphrase here drifts from the route the day the
        route's wording changes — which is exactly what happened to the boundary
        sentence itself, see the route's comment.
      */}
      <SgdsAlert show variant="info">
        <span>{report.boundary}</span>
      </SgdsAlert>
      <p className="text-sm text-[var(--sgds-body-color-subtle)]">{report.scheduler}</p>

      {report.nextAfterReference !== null && (
        <div>
          <SgdsButton
            variant="outline"
            type="button"
            disabled={continuing}
            onClick={() => {
              onContinue(report.nextAfterReference ?? '')
            }}
          >
            {continuing ? 'Continuing…' : 'Continue from here'}
          </SgdsButton>
          <p className="mt-1 text-xs text-[var(--sgds-body-color-subtle)]">
            Runs the sweep again starting after{' '}
            <code className="pt-mono text-xs">{report.nextAfterReference}</code>, with the same dry-run setting.
            Pressing <strong>Run sweep</strong> instead would examine the same bills over again.
          </p>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold">
          {report.entries.length === 0 ? 'No bill moved or was referred' : `${report.entries.length} bill(s) affected`}
        </h3>
        {report.entries.length === 0 ? (
          <p className="mt-1 text-sm text-[var(--sgds-body-color-subtle)]">
            Which is not the same as &ldquo;the estate agrees&rdquo;: read the counts above, and the boundary.
          </p>
        ) : (
          <>
            <SortAnnouncement
              sort={sort}
              interacted={interacted}
              busy={false}
              columnLabels={ENTRY_COLUMN_LABELS}
              rowCount={entries.length}
            />
            <div className="pt-table-wrap mt-2">
              <table className="pt-table">
                <caption className="sr-only">
                  Bills this run re-prorated or referred, with the co-payment movement on each
                </caption>
                <thead>
                  <tr>
                    <SortableTh label={ENTRY_COLUMN_LABELS.reference} sortKey="reference" sort={sort} onSort={onSort} />
                    <SortableTh label={ENTRY_COLUMN_LABELS.officerId} sortKey="officerId" sort={sort} onSort={onSort} />
                    <SortableTh
                      label={ENTRY_COLUMN_LABELS.agencyCode}
                      sortKey="agencyCode"
                      sort={sort}
                      onSort={onSort}
                    />
                    <SortableTh label={ENTRY_COLUMN_LABELS.outcome} sortKey="outcome" sort={sort} onSort={onSort} />
                    <SortableTh
                      label={ENTRY_COLUMN_LABELS.delta}
                      sortKey="delta"
                      sort={sort}
                      onSort={onSort}
                      align="right"
                    />
                    <SortableTh
                      label={ENTRY_COLUMN_LABELS.newCoPayment}
                      sortKey="newCoPayment"
                      sort={sort}
                      onSort={onSort}
                      align="right"
                    />
                    <th
                      scope="col"
                      data-unsortable="two values in one cell — the entitlement before and the entitlement after — so there is no single value for a sort to order the column by"
                    >
                      Entitlement, before and after
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.reference}>
                      <td>
                        <code className="pt-mono text-xs">{entry.reference}</code>
                      </td>
                      <td>{entry.officerId}</td>
                      <td>{entry.agencyCode}</td>
                      <td>
                        <CategoryBadge kind="state" value={entry.outcome} label={entry.outcome === 'REPRORATED' ? 'Re-prorated' : 'Referred'} />
                        {/*
                          ⚠ The referral reason is the OPERATOR's sentence, and it
                          belongs on the operator's screen. `ra-reproration-alert.ts`
                          deliberately does NOT put it in the file — it names
                          internal modules, A-15 and Q-27, and a counterparty's
                          parser has no business seeing them.
                        */}
                        {entry.referralReason !== null && (
                          <span className="block text-xs text-[var(--sgds-body-color-subtle)]">
                            {plainProse(entry.referralReason)}
                          </span>
                        )}
                      </td>
                      <td className="pt-num">{signedMoney(entry.newCoPayment - entry.priorCoPayment)}</td>
                      <td className="pt-num">
                        {formatCents(entry.newCoPayment)}
                        <span className="block text-xs text-[var(--sgds-body-color-subtle)]">
                          was {formatCents(entry.priorCoPayment)}
                        </span>
                      </td>
                      <td>
                        <code className="pt-mono text-xs">{entry.priorEntitlement}</code>
                        <span className="block text-xs text-[var(--sgds-body-color-subtle)]">
                          now <code className="pt-mono text-xs">{entry.newEntitlement}</code>, {plainCode(entry.cause)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function AlertReport({ report }: { readonly report: ReprorationAlertResponse }): ReactElement {
  if (report.outcome === 'NOTHING_TO_EMIT') {
    return (
      <SgdsAlert show variant="info">
        <span>
          <strong>No file was produced, and nothing failed.</strong> {plainProse(report.message ?? '')}
        </span>
      </SgdsAlert>
    )
  }
  return (
    <div className="flex flex-col gap-2">
      <SgdsAlert show variant="success">
        <span>
          <strong>Alert file produced for {report.counterpartyCode}.</strong> {report.recordCount} record(s) for
          business date {report.businessDate}.
        </span>
      </SgdsAlert>
      <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2 max-w-2xl">
        <div className="col-span-2 grid grid-cols-subgrid items-start">
          <dt className="text-sm">
            File
            <span className="block text-xs text-[var(--sgds-body-color-subtle)]">
              Held in the outbound file register. Find its contents under Records.
            </span>
          </dt>
          <dd className="text-sm font-semibold">
            <code className="pt-mono text-xs">{report.fileId}</code>
          </dd>
        </div>
        <div className="col-span-2 grid grid-cols-subgrid items-start">
          <dt className="text-sm">
            Net co-payment movement
            {/*
              ⚠ NAMED FOR WHAT IT IS. An alert file's trailer is the net change in
              what a Recovery Agency must recover — not a payable. It can
              legitimately be negative, or zero on a day where the movements
              cancel, and both are correct.
            */}
            <span className="block text-xs text-[var(--sgds-body-color-subtle)]">
              The trailer total. Not money to be paid: the net change in what this agency must recover. Negative
              and zero are both correct.
            </span>
          </dt>
          <dd className="pt-num text-sm font-semibold">
            {report.netCoPaymentDeltaCents === undefined ? 'Not applicable' : signedMoney(report.netCoPaymentDeltaCents)}
          </dd>
        </div>
        <div className="col-span-2 grid grid-cols-subgrid items-start">
          <dt className="text-sm">Content digest</dt>
          <dd className="text-sm font-semibold">
            <code className="pt-mono text-xs">{report.contentDigest}</code>
          </dd>
        </div>
      </dl>
      {report.boundary !== undefined && (
        <p className="text-sm text-[var(--sgds-body-color-subtle)]">{report.boundary}</p>
      )}
    </div>
  )
}

export function ReprorationConsolePage(): ReactElement {
  /**
   * ⚠ **Checked on load, and that is the whole safety posture of this screen.**
   * A money-moving bulk action whose destructive mode is the default is one
   * mis-click from a re-division nobody asked for.
   */
  const [dryRun, setDryRun] = useState(true)
  const [sweep, setSweep] = useState<ReprorationSweepResponse | string | null>(null)
  const [sweeping, setSweeping] = useState(false)
  const [continuing, setContinuing] = useState(false)

  /**
   * ★★★ WAVE 24 — THE SECOND HALF OF THE SAFETY POSTURE.
   *
   * The `dryRun` default above used to be the whole of it, and its own comment
   * said so: *"a money-moving bulk action whose destructive mode is the default
   * is one mis-click from a re-division nobody asked for"*. Clearing the box was
   * one click and pressing the button was the second — so the guard was a
   * checkbox, and nothing stood between the officer and an irreversible bulk
   * re-division of public money except having read the label.
   *
   * That is out of step with every sibling money write in this product: an
   * approval confirms (`ApprovalQueuePage`), a settlement release confirms
   * (`SettlementBatchesPage`), a parameter supersede confirms
   * (`ReferenceDataPage`). This screen moves the most money of any of them and
   * confirmed the least.
   *
   * ⚠ **The dry run is NOT confirmed, deliberately.** It writes nothing, and a
   * confirmation on a harmless action is how officers learn to dismiss the
   * confirmation on a harmful one. Only the branch that moves money asks.
   *
   * ⚠ **A CONTINUATION is confirmed too.** `SweepReport`'s "Continue from here"
   * pager calls the same sweep with the same `dryRun`, so the second page of a
   * live sweep moves money exactly as the first did. Gating only the button
   * would have left the pager as an unconfirmed door onto the same action — the
   * class this estate already names: a refusal that ships without covering
   * every way in.
   */
  const [confirming, setConfirming] = useState<{ readonly afterReference?: string } | null>(null)

  const [counterparty, setCounterparty] = useState('')
  const [alert, setAlert] = useState<ReprorationAlertResponse | string | null>(null)
  const [generating, setGenerating] = useState(false)
  /**
   * ★★ WAVE 26 — "Generate alert file" FREEZES a sentence and asks; the write
   * happens only from the confirmation (`ConfirmWrite`'s header says why every
   * irreversible write does this now). The sweep above already asks through its
   * own modal and the dry run deliberately does not — this closes the one door on
   * this screen that still acted on a single click.
   *
   * ⚠ Why it is worth asking: the database allows ONE file per Recovery Agency
   * per business date. A file produced for the wrong (but valid) agency code
   * uses up that agency's slot for today — the 409 the card below explains —
   * and today's rows are carried by a file addressed to the wrong counterparty.
   * The sentence is composed here, once, from the code as typed when the button
   * was pressed, never re-read from a field the officer may since have edited.
   */
  const [confirmingAlert, setConfirmingAlert] = useState<string | null>(null)

  /**
   * ★ A HINT, and the caveat is not optional.
   *
   * ⚠ **This is the CONTACT register, not the server's list of valid
   * addressees.** No route publishes the latter; the alert route validates the
   * code itself and names the registered Recovery Agencies in its refusal. So an
   * agency with no contact row is missing from this hint while still being a
   * valid addressee, and the screen must say so rather than presenting these as
   * the choices. A `<SgdsSelect>` here would make a derived list look
   * authoritative, which is why the control is a free-text field with chips
   * beside it.
   */
  const recoveryContacts = useApi(() => api.contacts({ kind: 'RECOVERY', limit: 50 }), [])
  const suggested = [...new Set((recoveryContacts.data?.contacts ?? []).map((c) => c.counterpartyCode))].sort()

  /**
   * The one door onto the sweep. Everything that starts one goes through here.
   *
   * A dry run runs immediately; a live run opens the confirmation and runs only
   * when it is accepted. Callers do not decide which — that is what stops a
   * later caller from being added without the gate.
   */
  const requestSweep = (afterReference?: string): void => {
    if (dryRun) {
      void runSweep(afterReference)
      return
    }
    setConfirming(afterReference === undefined ? {} : { afterReference })
  }

  const runSweep = async (afterReference?: string): Promise<void> => {
    const isContinuation = afterReference !== undefined
    if (isContinuation) setContinuing(true)
    else setSweeping(true)
    try {
      const answer = await api.runReprorationSweep({
        dryRun,
        ...(isContinuation && { afterReference }),
      })
      setSweep(answer)
    } catch (error: unknown) {
      // ⚠ The server's own message, verbatim. A 403 for a caller without the
      // full-audit-trail capability and a 500 are different sentences, and this
      // screen must not merge them into "the sweep failed".
      setSweep(
        error instanceof ApiError || error instanceof Error
          ? error.message
          : 'The sweep could not be completed and the server gave no reason.',
      )
    } finally {
      setSweeping(false)
      setContinuing(false)
    }
  }

  const requestAlert = (): void => {
    const code = counterparty.trim()
    if (code.length === 0) return
    setConfirmingAlert(
      `Produce today's Recovery Agency alert file addressed to ${code}. The database allows one file per Recovery Agency per business date, so a wrong code uses up today's slot for ${code} and a second attempt today is refused. There is no undo. A mistake is corrected by another record.`,
    )
  }

  const generate = async (): Promise<void> => {
    setConfirmingAlert(null)
    setGenerating(true)
    try {
      setAlert(await api.generateReprorationAlert(counterparty.trim()))
    } catch (error: unknown) {
      // ⚠ Verbatim, and this one matters more than most: the 409 for a code that
      // is not an active Recovery Agency NAMES the ones that are, and the 409 for
      // a second file on the same business date explains the one-per-day rule.
      // Both are the product working, and a screen that rendered "could not
      // generate the file" would hide the only useful sentence.
      setAlert(
        error instanceof ApiError || error instanceof Error
          ? error.message
          : 'The alert file could not be produced and the server gave no reason.',
      )
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Re-proration console</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
          Recovery Agency interface: re-dividing bills whose officers&apos; entitlement records
          have changed, and telling the Recovery Agency what moved. A sweep recomputes every bill in your scope against
          the entitlement in force today; where the two disagree it changes the split, or refers the bill to a person
          when money has already been instructed against the old division.
        </p>
      </div>

      <ConsoleBoundary />

      <section className="pt-card">
        <h2 className="text-xl font-semibold">1. Sweep for changed entitlements</h2>

        <label className="mt-3 flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1"
            checked={dryRun}
            onChange={(event) => {
              setDryRun(event.target.checked)
            }}
          />
          <span className="text-sm">
            <strong>Dry run: decide and report, write nothing.</strong>
            <span className="block text-xs text-[var(--sgds-body-color-subtle)]">
              Clear this and the sweep <strong>changes splits and moves money</strong>: every re-proration is written to
              the bill&apos;s ledger and the audit trail, and referred bills become records a Recovery Agency will be
              told about. Run it as a dry run first and read the counts.
            </span>
          </span>
        </label>

        {!dryRun && (
          <div className="mt-3">
            <SgdsAlert show variant="danger">
              <span>
                <strong>This will move public money.</strong> Splits will be re-divided between the government and
                officers for every bill whose recorded entitlement disagrees with the one in force today. There is no
                undo: a re-proration is an append-only ledger row, and reversing one is a further correction made by a
                person.
              </span>
            </SgdsAlert>
          </div>
        )}

        <div className="mt-4">
          <SgdsButton
            type="button"
            variant={dryRun ? 'primary' : 'danger'}
            disabled={sweeping || continuing}
            onClick={() => {
              requestSweep()
            }}
          >
            {sweeping ? 'Sweeping…' : dryRun ? 'Run sweep (dry run)' : 'Run sweep and move money'}
          </SgdsButton>
          {/* ★ WAVE 26 — a live region for the wait: the label change alone is silent
              to a screen reader, and this is the longest operation in the product. */}
          {sweeping && (
            <p role="status" className="mt-2 text-sm text-[var(--sgds-body-color-subtle)]">
              Sweeping the estate: the report appears below when it finishes.
            </p>
          )}
        </div>

        <div className="mt-4">
          {typeof sweep === 'string' ? (
            <ErrorCard title="The sweep did not run" message={sweep} onRetry={() => requestSweep()} />
          ) : sweep !== null ? (
            <SweepReport
              report={sweep}
              continuing={continuing}
              onContinue={(afterReference) => {
                requestSweep(afterReference)
              }}
            />
          ) : (
            <p className="text-sm text-[var(--sgds-body-color-subtle)]">
              Nothing has been swept in this session. Press the button above, and note that a sweep has never
              run on its own, so a quiet estate and an estate nobody has looked at read identically until you do.
            </p>
          )}
        </div>
      </section>

      {/*
        ★★★ The confirmation for the live sweep. Same shape as the approval and
        settlement-release confirmations, so an officer meets one pattern.

        ⚠ It states the SCOPE it is about to act on rather than a generic warning:
        a continuation acts on the bills after a named reference, and a reader who
        cannot tell which of the two they are confirming has not been asked
        anything useful.
      */}
      {/* N-14: mounted only while a sweep is being confirmed (as the approval queue does), so a closed modal never schedules a second update */}
      {confirming !== null && (
      <SgdsModal open onSgdsClose={() => setConfirming(null)} size="md">
        <div slot="header">Confirm a sweep that moves money</div>
        {confirming !== null && (
          <div className="flex flex-col gap-3">
            <p>
              This re-divides splits between the government and officers for every bill whose recorded entitlement
              disagrees with the one in force today
              {confirming.afterReference === undefined ? (
                ', across your whole scope'
              ) : (
                <>
                  , continuing from{' '}
                  <span className="pt-mono font-semibold">{confirming.afterReference}</span>
                </>
              )}
              .
            </p>
            <SgdsAlert show variant="danger">
              <span>
                <strong>There is no undo.</strong> Each re-proration is an append-only ledger row written against your
                identity, and referred bills become records a Recovery Agency will be told about. Reversing one is a
                further correction made by a person.
              </span>
            </SgdsAlert>
            <p className="text-sm text-[var(--sgds-body-color-subtle)]">
              To see what it would do without writing anything, cancel and tick <strong>Dry run</strong>.
            </p>
          </div>
        )}
        <div slot="footer" className="flex justify-end gap-2">
          <SgdsButton variant="ghost" type="button" onClick={() => setConfirming(null)}>
            Cancel
          </SgdsButton>
          <SgdsButton
            variant="danger"
            type="button"
            onClick={() => {
              const pending = confirming
              setConfirming(null)
              if (pending !== null) void runSweep(pending.afterReference)
            }}
          >
            Sweep and move money
          </SgdsButton>
        </div>
      </SgdsModal>
      )}

      <section className="pt-card">
        <h2 className="text-xl font-semibold">2. Produce the Recovery Agency alert file</h2>
        <p className="mt-1 text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
          One file per Recovery Agency per business date, enforced by the database. It carries every bill re-prorated or
          referred <strong>today</strong>, so a sweep run after today&apos;s file exists has its rows carried by
          no artefact until tomorrow, and a second attempt today is refused rather than producing a second file. A
          Recovery Agency deducts officers&apos; co-payments from payroll, which is why the layout leads with
          the co-payment movement.
        </p>

        <div className="mt-4 max-w-sm">
          <SgdsInput placeholder=""
            label="Recovery Agency code"
            hintText="The addressee of this file. The server refuses anything that is not an active Recovery Agency, and names the ones that are."
            value={counterparty}
            onSgdsInput={(event) => {
              setCounterparty(targetValue(event))
            }}
          />
        </div>
        {/*
          ★★ WAVE 24 — a failed CONTACT read used to render nothing at all.

          `recoveryContacts.error` was never read in this file, so a failure
          simply produced no chips — identical on screen to an estate that
          registers no Recovery Agency contacts. Those are opposite facts: one
          means "ask someone why the register is empty", the other means "the
          read failed and the hint below is missing, not empty".

          ⚠ Not an `ErrorCard`: this is a HINT beside a free-text field, and the
          field still works. A full error card would imply the SCREEN is broken
          when only its convenience is.
        */}
        {recoveryContacts.error !== null && (
          <p className="text-sm text-[var(--sgds-danger-color-emphasis)]" role="status">
            The addressee suggestions could not be loaded ({recoveryContacts.error}). The field below still
            works: type the Recovery Agency code, and the server names the registered agencies if it is
            not one of them.
          </p>
        )}
        {suggested.length > 0 && (
          <p className="mt-2 text-xs text-[var(--sgds-body-color-subtle)]">
            Agencies with a registered contact:{' '}
            {suggested.map((code) => (
              <button
                key={code}
                type="button"
                className="pt-chip pt-ref-link"
                onClick={() => {
                  setCounterparty(code)
                }}
              >
                {code}
              </button>
            ))}
            <span className="block">
              ⚠ Derived from the <strong>contact register</strong>, not from the server&apos;s list of valid addressees:
              no route publishes that. An agency with no contact row is missing here and is still a valid
              addressee; a code that is not an active Recovery Agency is refused by the server, which names the ones
              that are.
            </span>
          </p>
        )}

        <div className="mt-4">
          <SgdsButton
            type="button"
            disabled={generating || counterparty.trim().length === 0 || confirmingAlert !== null}
            onClick={() => {
              requestAlert()
            }}
          >
            {generating ? 'Producing…' : 'Generate alert file'}
          </SgdsButton>
          {dryRun && (
            <p className="mt-1 text-xs text-[var(--sgds-body-color-subtle)]">
              A dry run writes no ledger rows, so producing a file straight after one reports{' '}
              <strong>nothing to emit</strong> unless a real sweep has already run today.
            </p>
          )}
        </div>

        <div className="mt-4">
          {confirmingAlert !== null && (
            <ConfirmWrite
              summary={confirmingAlert}
              confirmLabel="Yes, produce it"
              variant="danger"
              onConfirm={() => void generate()}
              onCancel={() => setConfirmingAlert(null)}
            />
          )}
          {typeof alert === 'string' ? (
            /*
              ⚠ NOT an ErrorCard with a retry. The two refusals a caller actually
              meets here — a code that is not an active Recovery Agency, and
              today's file already existing — are the product working correctly,
              and offering "try again" against either would be inviting the
              operator to repeat something that will refuse for the same reason.
            */
            <SgdsAlert show variant="warning">
              <span>{alert}</span>
            </SgdsAlert>
          ) : alert !== null ? (
            <AlertReport report={alert} />
          ) : null}
        </div>
      </section>
    </div>
  )
}
